import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import Modal from 'react-bootstrap-modal'
import FlashAlert from '../flash-alert'
import GameDetails from './game-details'
import GameDetailsFooter from './game-details-footer'
import GameEvents from './game-events'
import GameNotes from './game-notes'
import CancelModal from '../cancel-game-modal'
import ClientModal from '../client-modal'
import ReceiptModal from '../history/receipt-modal'
import RefundModal from '../refund-modal'
import PaymentModal from '../../containers/payment-modal'
import RecurringReservations from '../../containers/recurring-reservations'
import GameForm from '../edit-game-form'
import ModalCalendar from '../../containers/modal-calendar'
import { formatPhoneNumberIntl } from 'react-phone-number-input'

function GameModal(props) {
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [showPlayer, setShowPlayer] = useState(null)
  const [showReceipt, setShowReceipt] = useState(null)
  const [form, setForm] = useState(null)
  const [showEvents, setShowEvents] = useState(false)
  const [showNotes, setShowNotes] = useState(false)
  const [showReccurings, setShowRecurrings] = useState(false)
  const alert = props.alert
  const game = props.game

  useEffect(() => {
    props.loadGame(props.gameId)
    return () => {
      props.clearGame()
    }
  }, [])

  const updatePlayer = (player) => {
    const name = [player.first_name, player.last_name].filter(n => n).join(' ')
    props.onUpdatePlayer({id: showPlayer, name: name, phone: formatPhoneNumberIntl(player.phone), fake: player.fake})
    setShowPlayer(null)
    props.showAlert({ text: 'Client update success', type: 'success' })
  }

  const updateGame = (data) => {
    setForm(null)
    props.onClose()
  }

  const updateRecurring = () => {
    setForm(null)
    props.onClose()
  }

  const cancelGame = () => {
    setShowCancelModal(false)
    props.onClose()
  }

  const confirmGame = () => {
    props.onConfirm()
    props.onClose()
  }

  const noShowGame = () => {
    props.onNoShow()
    props.onClose()
  }

  const sortedPlayerReservations = () => {
    return props.player_reservations.sort((a, b) => {
      if (a.is_game_master || b.is_game_master) return b.is_game_master - a.is_game_master
      if (a.fully_paid || b.fully_paid) return b.fully_paid - a.fully_paid

      return a.displayed_name > b.displayed_name ? 1 : -1
    })
  }

  if (!game) return null

  return <Modal container={document.body} show={!!game} onHide={props.onClose} dialogClassName='gameModal'>
    { showCancelModal && <CancelModal game={game} onClose={() => setShowCancelModal(false)} onCancel={cancelGame} /> }
    { showPlayer && <ClientModal playerId={showPlayer} onClose={() => setShowPlayer(null)} onUpdate={updatePlayer} /> }
    { showReceipt && <ReceiptModal receiptId={showReceipt} onClose={e => setShowReceipt(false)} /> }
    { !!props.payment && 
      <PaymentModal
        game={game}
        playerReservation={props.player_reservations.find(pr => pr.id == props.payment)}
        socketUrl={props.socketUrl}
        onClose={() => props.onClearPayment()}
      /> 
    }
    { !!props.transaction &&
      <RefundModal
        transaction={props.transaction}
        onClose={props.onCancelRefund}
        onConfirm={props.onRefundPayment} 
        onConfirmReceipt={props.onRefundReceipt} 
      /> 
    }
    { form && 
      <GameForm
        id={form.gameId}
        recurringId={form.recurringId}
        onClose={() => setForm(null)}
        onUpdate={updateGame}
        onUpdateRecurring={updateRecurring}
      /> 
    }
    { props.modalCalendar && 
      <ModalCalendar
        id={props.modalCalendar}
        onClose={() => props.onCloseCalendar()}
        onLinked={props.onPaymentLinked}
      /> 
    }
    
    <Modal.Header closeButton>
      <Modal.Title>{game.special_event ? 'Special' : 'Reservation'}</Modal.Title>
    </Modal.Header>
    <ul className='nav nav-tabs'>
      <li role='presentation'>
        <a data-toggle='tab' href='#game-details'>Details</a>
      </li>
      <li role='presentation'>
        <a data-toggle='tab' href='#game-history' onClick={() => setShowEvents(true)}>History</a>
      </li>
      <li role='presentation'>
        <a data-toggle='tab' href='#game-recurrings' onClick={() => setShowRecurrings(true)}>Recurring</a>
      </li>
      <li role='presentation'>
        <a data-toggle='tab' href='#game-comment' onClick={() => setShowNotes(true)}>Notes</a>
      </li>
    </ul>

    <div className={`tab-content${props.isLoading ? ' isLoading' : ''}`}>
      <div id='game-details' className='tab-pane fade in active'>
        <Modal.Body>
          { alert && <FlashAlert text={alert.text} type={alert.type} onHide={props.clearAlert} />}
          { props.isLoading && <div className='animated-spinner' /> }
          <GameDetails
            game={game}
            playerReservations={sortedPlayerReservations()}
            priceEditable={props.price_editable}
            onEditCost={props.onEditCost}
            onAddPlayer={props.onAddPlayer}
            onPlayersCount={props.onPlayersCount}
            onRenamePlayer={props.onRenamePlayer}
            onEditPlayerCost={props.onEditPlayerCost}
            onDeletePlayer={props.onDeletePlayer}
            onShowPlayer={(playerId) => setShowPlayer(playerId)}
            onShowReceipt={(id) => setShowReceipt(id)}
            onRefundPaidFor={props.onRefundPaidFor}
            onRefund={props.onStartRefund}
            onEdit={() => setForm({gameId: game.id})}
            onPayment={(id) => props.onStartPayment(id)}
            onChangeReservation={props.onChangeReservation}
          />
        </Modal.Body>
        <Modal.Footer>
          <GameDetailsFooter 
            game={game}
            onClose={props.onClose}
            onNoShow={noShowGame}
            onCancel={() => setShowCancelModal(true)}
            onConfirm={confirmGame}
          />
        </Modal.Footer>
      </div>
      <div id='game-history' className='tab-pane fade'>
        <Modal.Body>
          { showEvents && <GameEvents events={props.events} onLoad={props.onLoadEvents} /> }
        </Modal.Body>
      </div>
      <div id='game-recurrings' className='tab-pane fade'>
        <Modal.Body>
          { props.isLoading && <div className='animated-spinner' /> }
          { showReccurings && 
            <RecurringReservations 
              reservationId={game.id}
              defaultDate={game.date} 
              onEdit={(data) => setForm(data)}
              onChanged={props.onClose}
            />
          }
        </Modal.Body>
      </div>
      <div id='game-comment' className='tab-pane fade'>
        <Modal.Body>
          { showNotes &&
            <GameNotes 
              notes={props.notes}
              onLoadNotes={props.onLoadNotes}
              onCreateNote={props.onCreateNote} 
              onEditNote={props.onEditNote} 
              onRemoveNote={props.onRemoveNote} 
            />
          }
        </Modal.Body>
      </div>
    </div>
  </Modal>
}

GameModal.propTypes = {
  gameId: PropTypes.number,
  socketUrl: PropTypes.string,
  game: PropTypes.object,
  player_reservations: PropTypes.array,
  events: PropTypes.array,
  notes: PropTypes.array,
  price_editable: PropTypes.bool,
  alert: PropTypes.object,
  payment: PropTypes.number,
  transaction: PropTypes.object,
  modalCalendar: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  clearGame: PropTypes.func.isRequired,
  showAlert: PropTypes.func.isRequired,
  clearAlert: PropTypes.func.isRequired,
  loadGame: PropTypes.func.isRequired,
  onEditCost: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onAddPlayer: PropTypes.func.isRequired,
  onRenamePlayer: PropTypes.func.isRequired,
  onDeletePlayer: PropTypes.func.isRequired,
  onRefundPaidFor: PropTypes.func.isRequired,
  onStartRefund: PropTypes.func.isRequired,
  onRefundPayment: PropTypes.func.isRequired,
  onRefundReceipt: PropTypes.func.isRequired,
  onStartPayment: PropTypes.func.isRequired,
  onClearPayment: PropTypes.func.isRequired,
  onCreateNote: PropTypes.func.isRequired,
  onEditNote: PropTypes.func.isRequired,
  onRemoveNote: PropTypes.func.isRequired,
  onChangeReservation: PropTypes.func.isRequired,
  onCloseCalendar: PropTypes.func.isRequired,
  onPaymentLinked: PropTypes.func.isRequired,
  onUpdateGame: PropTypes.func.isRequired,
}

export default GameModal
